import { useTranslations } from '@vocab/react';
import { Heading, Stack } from 'braid-design-system';
import type React from 'react';
import { Helmet } from 'react-helmet-async';

import { useAppContext } from '../../AppContext';

import translations from './.vocab';

export type Title =
  | 'Sign in'
  | 'Register'
  | 'Register as an employer'
  | 'Forgot password'
  | 'Expired password'
  | 'Reset password'
  | 'Activate account'
  | 'Check your email for a code';

type HeadingProps = React.ComponentProps<typeof Heading>;

interface FormHeaderProps {
  title?: Title;
  renderTitle?: boolean;
  titleAlignment?: HeadingProps['align'];
  titleLevel?: HeadingProps['level'];
}

const FormHeader = ({
  title,
  renderTitle = true,
  titleAlignment,
  titleLevel = '1',
}: FormHeaderProps) => {
  const { t } = useTranslations(translations);
  const { siteName, experience } = useAppContext();

  const experienceTitles = {
    business: 'Sign In - SEEK' as const,
    candidate: 'Candidate Sign In - SEEK' as const,
    hirer: 'Employer Sign In - SEEK' as const,
  };

  const experienceDescriptions = {
    business:
      'Find a Business or Franchise for sale. SEEK Business offers you the unique opportunity of owning & managing your own business.' as const,
    candidate:
      'Sign in to view and manage your saved jobs, apply for new jobs and streamline your job seeking process.' as const,
    hirer:
      'Find your next hire on Australia’s largest employment marketplace! With 96 roles filled hourly, we place more people in more jobs. Login &amp; start finding talent.' as const,
  };

  return (
    <Stack space="none">
      {(() => {
        switch (title) {
          case 'Sign in':
            return (
              <Helmet>
                <title>
                  {t(experienceTitles[experience], {
                    siteName,
                  })}
                </title>
                <meta
                  name="description"
                  content={t(experienceDescriptions[experience], {
                    siteName,
                  })}
                />
              </Helmet>
            );
          case 'Reset password': {
            return (
              <Helmet>
                <title>{t('Reset Password - SEEK', { siteName })}</title>
                <meta
                  name="description"
                  content={t('Reset Password - SEEK', { siteName })}
                />
              </Helmet>
            );
          }
          case 'Activate account': {
            return (
              <Helmet>
                <title>{t('Activate account - SEEK', { siteName })}</title>
                <meta
                  name="description"
                  content={t('Activate account - SEEK', { siteName })}
                />
              </Helmet>
            );
          }
          case 'Register': {
            return (
              <Helmet>
                <title>
                  {t('Register with SEEK, Never miss an opportunity - SEEK', {
                    siteName,
                  })}
                </title>
                <meta
                  name="description"
                  content={t(
                    'Register with SEEK &amp; never miss an opportunity. Set up JobMails, streamline your job seeking process &amp; much more. Create your SEEK Profile today.',
                    {
                      siteName,
                    },
                  )}
                />
              </Helmet>
            );
          }
          case 'Forgot password': {
            return (
              <Helmet>
                <title>
                  {t('Forgot Password - SEEK', {
                    siteName,
                  })}
                </title>
                <meta
                  name="description"
                  content={t('Forgot Password - SEEK', {
                    siteName,
                  })}
                />
              </Helmet>
            );
          }
          case 'Check your email for a code': {
            return (
              <Helmet>
                <title>
                  {t('Check your email for a code - SEEK', {
                    siteName,
                  })}
                </title>
                <meta
                  name="description"
                  content={t('Check your email for a code - SEEK', {
                    siteName,
                  })}
                />
              </Helmet>
            );
          }
        }
      })()}

      {renderTitle && title && (
        <Heading align={titleAlignment} level={titleLevel}>
          {t(title)}
        </Heading>
      )}
    </Stack>
  );
};

export default FormHeader;
